/**
 * 时长档行(2026-09-13 D 批)—— 目的流两栏布局把时长从「时长×拍法 联动卡」里拆出来
 * **单独占一行**(设计稿 7d586023 V4 左栏第一行),拍法/节拍带留在联动卡里。
 *
 * ★为什么抽成组件而不是在页面里再抄一遍 chips:时长档 chips + 提示 + 「装得下多少」建议行
 *   此前散在 `VideoShootPlanCard` ① 与 `VideoFormatRow` 两处;D 批再多一个「独立时长卡」出处,
 *   三处各写一遍必漂。这里收成**无边框内容块**(卡壳由调用方给,避免「卡中卡」),
 *   `VideoShootPlanCard`(showDuration 时)与目的流独立时长卡都引它,值/口径单一源。
 *   档值仍复用 `DURATION_OPTIONS`(VideoFormatRow 导出的单一映射)。
 */
import type { Dispatch, SetStateAction } from "react";

import { useCreativeTaxonomy } from "../../model/useCreativeTaxonomy";
import { briefTargetSec } from "../../model/effectiveDuration";
import type { FormState } from "../../model/videoCreateTypes";

import { DurationAdviceLine } from "./DurationAdviceLine";
import { DURATION_OPTIONS } from "./VideoFormatRow";
import { OptionChip, OptionChipGroup } from "./OptionChip";

interface Props {
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  disabled?: boolean;
}

export function VideoDurationTierRow({ form, setForm, disabled }: Props) {
  // 「这个档装得下多少」的真值:引擎节拍表内容位数(与 VideoFormatRow / VideoShootPlanCard 同
  // queryKey/staleTime,缓存命中零多拉)。taxonomy 未就绪 → DurationAdviceLine 回退经验值。
  const taxonomyQuery = useCreativeTaxonomy(form.toneRegister);
  const targetSec = briefTargetSec(form.briefDuration);
  const contentBeats = taxonomyQuery.data?.purposes
    ?.find((p) => p.purpose === form.briefPurpose)
    ?.beatPlans?.find((b) => b.targetSec === targetSec)?.contentBeats;

  return (
    <div data-video-duration-tier-row>
      <span className="mb-1.5 block text-sm text-muted-foreground">时长</span>
      <OptionChipGroup label="目标时长">
        {DURATION_OPTIONS.map((d) => {
          const active = form.briefDuration === d.value;
          return (
            <OptionChip
              key={d.value}
              radio
              active={active}
              tabIndex={active ? 0 : -1}
              disabled={disabled}
              onClick={() => setForm((f) => ({ ...f, briefDuration: d.value }))}
            >
              {d.label}
            </OptionChip>
          );
        })}
        {/**
         * W4(2026-09-13):「AI 按内容定」—— 不选档,把时长交给引擎按文案/卖点量推断。
         * briefDuration 置 null(不是空串):durationTier 不发,引擎按 `estimatedDurationSec`
         * 自己给答案(编排后交代卡的「预计成片 Xs」就是它)。durationFit 对 briefTargetSec
         * 返回的 undefined 已天然处理为「不显合身度」,这里不用额外分支。
         */}
        <OptionChip
          radio
          active={!form.briefDuration}
          tabIndex={!form.briefDuration ? 0 : -1}
          disabled={disabled}
          data-testid="duration-tier-ai-decide"
          onClick={() => setForm((f) => ({ ...f, briefDuration: null }))}
        >
          AI 按内容定
        </OptionChip>
      </OptionChipGroup>
      <p className="mt-1.5 max-w-[24rem] text-2xs leading-snug text-muted-foreground/80">
        时长是目标值,AI 按它编排分镜与口播,成片可能有 3 秒左右出入。
      </p>
      {/* 这个档装得下多少(共享行,措辞/口径单一源)。 */}
      <DurationAdviceLine targetSec={targetSec} contentBeats={contentBeats} />
    </div>
  );
}
